import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api/client';
import type { BannerAd, BannerAdInput } from './types';

/**
 * Banner ads.
 *
 * The controller publishes eight read routes; seven of them are the storefront
 * and app consumers — `bannerads`, `list/bannerads`, `app/bannerads` and their
 * per-store variants, all `[AllowAnonymous]` and all pre-filtered to what is
 * currently running. The admin wants the opposite, so it uses the eighth:
 * `bannerads/all` returns every non-deleted banner whatever its state, and is
 * the only one of the eight that checks a role (SuperAdmin or Marketing).
 *
 * Writes are all POST, including the delete.
 */

const KEY = ['banner-ads'] as const;

interface PagedBanners {
  results: BannerAd[];
}

export function useBannerAds() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<PagedBanners>('businesses/bannerads/all'),
    select: (data) =>
      // Unordered from the server — it reads the table with no `OrderBy` — so
      // the running order the `ordering` column describes is applied here.
      [...(data.results ?? [])].sort(
        (a, b) =>
          a.ordering - b.ordering ||
          (a.name ?? '').localeCompare(b.name ?? '')
      ),
    staleTime: 60_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/** Create and update are separate routes taking the identical body. */
export function useSaveBannerAd() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, values }: { id: string | null; values: BannerAdInput }) =>
      post<unknown>(
        id ? `businesses/bannerad/update/${id}` : 'businesses/bannerad/create',
        values
      ),
    onSuccess: invalidate,
  });
}

/** A soft delete — the row keeps a `DeletedAt`, so "archive" is honest here. */
export function useDeleteBannerAd() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      post<unknown>(`businesses/bannerad/delete/${id}`, {}),
    onSuccess: invalidate,
  });
}
